import { useState, type FormEvent } from "react";
import { Link, Redirect, useLocation } from "wouter";
import { useApiClient } from "../api/ApiProvider";
import { useAuth } from "../auth/AuthContext";
import { Button } from "../components/Button";
import "./login.css";

export function RegisterPage() {
  const { session, adoptSession } = useAuth();
  const [, navigate] = useLocation();
  const client = useApiClient();

  const [username, setUsername] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  if (session) {
    return <Redirect to="/" replace />;
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setError(null);

    if (password !== confirmPassword) {
      setError("Passwords do not match.");
      return;
    }

    setIsSubmitting(true);
    try {
      const result = await client.register({
        username: username.trim(),
        password,
        displayName: displayName.trim(),
      });
      adoptSession(result);
      navigate("/", { replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Registration failed. Try again.");
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <div className="login">
      <div className="login__card">
        <div className="login__brand">
          <span className="login__brand-mark" aria-hidden="true" />
          <span className="login__brand-name">Comebye</span>
        </div>
        <p className="login__tagline">
          Develop an app, get it running on your own servers, and keep it there — no lock-in to a
          platform that only rents you back your own infrastructure.
        </p>
        <p className="login__subtitle">Create your account to get started</p>

        <form className="login__form" onSubmit={(e) => void handleSubmit(e)} noValidate>
          {error && (
            <p className="login__error" role="alert">
              {error}
            </p>
          )}

          <label className="login__field">
            <span>Display name</span>
            <input
              type="text"
              name="displayName"
              autoComplete="name"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              required
            />
          </label>

          <label className="login__field">
            <span>Username</span>
            <input
              type="text"
              name="username"
              autoComplete="username"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              required
            />
          </label>

          <label className="login__field">
            <span>Password</span>
            <input
              type="password"
              name="password"
              autoComplete="new-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
          </label>

          <label className="login__field">
            <span>Confirm password</span>
            <input
              type="password"
              name="confirmPassword"
              autoComplete="new-password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
            />
          </label>

          <Button type="submit" variant="primary" disabled={isSubmitting} className="login__submit">
            {isSubmitting ? "Creating account…" : "Create account"}
          </Button>
        </form>

        <p className="login__note">
          Already have an account? <Link to="/login">Sign in</Link>
        </p>
      </div>
    </div>
  );
}
